import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { Link, NavLink, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { useT } from '../i18n';
import { FREE_SHIPPING_MINOR, formatCzk } from '../money';
import { AccountMenu } from './AccountMenu';
import { CartMenu } from './CartMenu';
import { CurrencySwitcher } from './CurrencySwitcher';
import { LanguageSwitcher } from './LanguageSwitcher';
import { FavoritesLink } from './FavoritesLink';
import { Logo } from './Logo';
import { NewsletterSignup } from './NewsletterSignup';
import { PollWidget } from './PollWidget';
import { LayerHost } from '../layers/LayerHost';
import { SearchBox } from './SearchBox';
import { SocialLinks } from '../storefront/SocialLinks';
import { CookieBanner } from '../tracking/CookieBanner';
import { useConsent } from '../tracking/consent';

export function Layout({ children }: { children: ReactNode }) {
  const scrolled = useScrolled(12);
  const { t } = useT();
  const { openBanner } = useConsent();

  return (
    <div className="shell">
      {/* Klavesnicova zkratka pres navigaci rovnou na obsah. */}
      <a className="skip-link" href="#obsah">
        {t('skip.toContent')}
      </a>

      {/* Oznamovaci lista v toku dokumentu nad hlavickou (zadny CLS skok). */}
      <LayerHost region="announcement" />

      {/* Pruh se sluzbami. Vedle sebe stoji to, na co se zakaznici ptaji
          nejcasteji – a co je zaroven duvod, proc nakoupit prave tady.
          Vpravo prepinac jazyka (Faze 2). */}
      <div className="service-bar">
        <a className="service-bar-phone" href="tel:+420739626040">
          <PhoneIcon />
          <span className="service-phone-num">739&nbsp;626&nbsp;040</span>
          <span className="service-phone-hours">8:30–17:00</span>
        </a>
        <div className="service-bar-items">
          <span>{t('bar.sameDay')}</span>
          <span>{t('bar.freeShip', { amount: formatCzk(FREE_SHIPPING_MINOR) })}</span>
          <span>{t('bar.returns')}</span>
        </div>
        <div className="service-bar-switches">
          <AccountMenu />
          <CurrencySwitcher />
          <LanguageSwitcher />
        </div>
      </div>

      {/*
        Tri sloupce: znacka vlevo, hledani uprostred, ucet a kosik vpravo.
        Prostredni sloupec je pruzny, krajni ne – hledani tak roste s oknem
        a logo s kosikem zustavaji zakotvene u okraju.
      */}
      {/* Hlavička s hledáním a kategoriový pruh drží nahoře společně –
          obalový .header-sticky je přichytí, service bar nad nimi odscrolluje. */}
      <div className="header-sticky">
        <header className={scrolled ? 'topbar is-scrolled' : 'topbar'}>
          <Link to="/" className="brand" aria-label="Super Krmivo – domů">
            <Logo />
          </Link>

          <SearchBox />

          <nav className="topnav" aria-label="Účet a košík">
            <NavLink
              to="/objednavka"
              className={({ isActive }) => (isActive ? 'order-link active' : 'order-link')}
            >
              <OrderIcon />
              <span>{t('nav.myOrder')}</span>
            </NavLink>
            <FavoritesLink />
            <CartMenu />
          </nav>
        </header>

        <CategoryBar />
      </div>

      <main className="page" id="obsah">
        {children}
      </main>

      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <Logo />
            <p className="muted small">{t('footer.tagline')}</p>
            {/* Sociální sítě (#31). Adresy jsou v konfiguraci, ne v kódu —
                viz `storefront/social.ts`. Když není nastavená ani jedna,
                blok se vůbec nevykreslí. */}
            <SocialLinks />
          </div>

          <NewsletterSignup />

          {/* Anketa (#29): běží nejvýš jedna, jinak se blok vůbec nevykreslí. */}
          <PollWidget />

          {/* Odkaz patri do paticky, ne do hlavni navigace: hleda ho ten,
              kdo uz objednal, ne ten, kdo si prohlizi zbozi. */}
          <nav className="footnav">
            <Link to="/objednavka">Kde je moje objednávka</Link>
            <Link to="/kosik">Košík</Link>
            <Link to="/slovnik">{t('glossary.footerLink')}</Link>
            <Link to="/kontakt">Kontakt</Link>
            {/* Znovuotevře cookie lištu – volbu jde kdykoli změnit. */}
            <button type="button" className="footnav-button" onClick={openBanner}>
              {t('footer.cookieSettings')}
            </button>
          </nav>

          <FooterPages />
        </div>

        <p className="muted small footer-note">Super Krmivo · chovatelské potřeby</p>
      </footer>

      {/* Pop-upy a plovoucí widgety (Fáze 7) – nad obsahem, pod cookie lištou.
          Uvítací popup je teď kampaň (převedená migrací 055). */}
      <LayerHost region="overlay" />

      {/* Cookie lišta – ukáže se, dokud zákazník nerozhodl; bez freeze UI. */}
      <CookieBanner />
    </div>
  );
}

/** Ikona balíku u odkazu na objednávku – stejný stroke styl jako srdce a košík. */
function OrderIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        d="M21 8 12 3 3 8v8l9 5 9-5V8Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path
        d="m3 8 9 5 9-5M12 13v8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="m7.5 5.5 9 5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Telefon v horním pruhu služeb. */
function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" focusable="false">
      <path
        d="M6.5 3h3l1.5 5-2 1.5a11 11 0 0 0 5 5l1.5-2 5 1.5v3a2 2 0 0 1-2 2A16 16 0 0 1 4.5 5a2 2 0 0 1 2-2Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Malá šipka dolů na položkách s rozbalovacím mega-menu. */
function ChevronIcon() {
  return (
    <svg
      className="cat-caret"
      viewBox="0 0 24 24"
      width="12"
      height="12"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="m6 9 6 6 6-6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Podkategorie ve stromu navigace. */
interface CategoryChild {
  readonly slug: string;
  readonly name: string;
}
/** Top-level kategorie s podkategoriemi. */
interface CategoryNode extends CategoryChild {
  readonly children: readonly CategoryChild[];
}

/**
 * Slug → index ikony ve spritu `public/category-icons.png` (5 sloupců × 2 řádky).
 * Pořadí odpovídá pevnému pořadí kategorií v pruhu.
 */
const ICON_INDEX: Readonly<Record<string, number>> = {
  'zachran-krmivo': 0,
  akce: 1,
  psi: 2,
  kocky: 3,
  hlodavci: 4,
  ptaci: 5,
  novinky: 6,
  'pro-panicky': 7,
  teraristika: 8,
  znacka: 9,
};

/** Slug „Akce" – vede na vlastní stránku výprodeje a nese oranžovou identitu. */
const SALE_SLUG = 'akce';

/**
 * Výřez ikony ze spritu jako `mask-position`. Ikona je zelená na průhledném
 * pozadí, přebarvuje se `currentColor` – proto se s textem překlopí i na hover.
 * Sprite má 5 sloupců a 2 řádky, takže krok je 25 % vodorovně a 100 % svisle.
 */
function iconStyle(slug: string): CSSProperties | undefined {
  if (!(slug in ICON_INDEX)) return undefined;
  const url = `url("/icons/${slug}.png")`;
  return { maskImage: url, WebkitMaskImage: url };
}

/** Kolik sloupců dostane mega-menu podle počtu podkategorií (velké = víc). */
function megaColumns(count: number): number {
  return Math.min(4, Math.max(1, Math.ceil(count / 8)));
}

/**
 * Rozcestnik kategorii pod hlavickou.
 *
 * Pevnych 10 top-level polozek se strom nacte jednou z /api/shop/category-tree.
 * Kdyz se nenacte (prazdny strom / chyba), pruh se nevykresli vubec – prazdna
 * lista pod hlavickou vypada jako chyba sablony.
 */
function CategoryBar() {
  const [categories, setCategories] = useState<readonly CategoryNode[]>([]);
  const [params] = useSearchParams();
  const activeSlug = params.get('kategorie') ?? '';

  useEffect(() => {
    let alive = true;
    void api
      .listCategoryTree()
      .then((result) => {
        if (alive) setCategories(result.categories);
      })
      .catch(() => {
        if (alive) setCategories([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (categories.length === 0) return null;

  return (
    <nav className="category-bar" aria-label="Kategorie">
      <ul className="category-bar-inner">
        {categories.map((category) => (
          <CategoryItem key={category.slug} category={category} activeSlug={activeSlug} />
        ))}
      </ul>
    </nav>
  );
}

/**
 * Jedna položka pruhu: ikona + název, a u položek s dětmi rozbalovací mega-menu.
 *
 * Mega-menu se kreslí `position: fixed` a pozicuje podle `getBoundingClientRect`
 * spouštěče. Tím se schválně vymyká vodorovně scrollujícímu `.category-bar`
 * (jeho `overflow-x: auto` by panel rozbalený dolů uříznul) a kreslí se nad
 * obsahem stránky. Otevírá se na hover i focus, zavírá na mouseleave, Esc,
 * blur i ťuknutí mimo; na dotyku ho otevře první ťuknutí.
 */
function CategoryItem({
  category,
  activeSlug,
}: {
  category: CategoryNode;
  activeSlug: string;
}) {
  const hasChildren = category.children.length > 0;
  const isSale = category.slug === SALE_SLUG;
  // „Zachraň krmivo" je vlajková kategorie – dostane jemný shimmer, ať vyskočí.
  const isRescue = category.slug === 'zachran-krmivo';
  const to = isSale ? '/akce' : `/?kategorie=${encodeURIComponent(category.slug)}`;
  const isActive =
    activeSlug !== '' &&
    (activeSlug === category.slug || category.children.some((c) => c.slug === activeSlug));

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  const itemRef = useRef<HTMLLIElement>(null);
  const triggerRef = useRef<HTMLAnchorElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | undefined>(undefined);

  const clearClose = () => {
    if (closeTimer.current !== undefined) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = undefined;
    }
  };

  const openMenu = () => {
    if (!hasChildren) return;
    clearClose();
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setPos({ left: rect.left, top: rect.bottom });
    setOpen(true);
  };

  const closeMenu = () => {
    clearClose();
    setOpen(false);
  };

  // Krátká prodleva, ať se dá myší přejet z položky do panelu přes mezeru.
  const scheduleClose = () => {
    clearClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  };

  // Přepočet pozice panelu po otevření (a při scrollu/resize, dokud je otevřený).
  // Panel je fixed, takže ho `overflow` pruhu neuřízne; zde ho jen podržíme v okně.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const width = panelRef.current?.offsetWidth ?? 240;
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      setPos({ left, top: rect.bottom });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  // Ťuknutí/klik mimo položku zavře menu (hlavně dotyk, kde není hover).
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!itemRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // Uklidit timer při odmontování.
  useEffect(() => clearClose, []);

  const onKeyDown = (event: ReactKeyboardEvent<HTMLLIElement>) => {
    if (event.key === 'Escape' && open) {
      closeMenu();
      triggerRef.current?.focus();
    }
  };

  const onBlur = (event: ReactFocusEvent<HTMLLIElement>) => {
    if (!itemRef.current?.contains(event.relatedTarget as Node | null)) closeMenu();
  };

  const onTriggerClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    // Dotyk (bez hoveru): první ťuknutí otevře menu, druhé projde na odkaz.
    if (hasChildren && !open && window.matchMedia('(hover: none)').matches) {
      event.preventDefault();
      openMenu();
    }
  };

  const style = iconStyle(category.slug);
  const cols = megaColumns(category.children.length);

  return (
    <li
      ref={itemRef}
      className="cat-item"
      {...(hasChildren
        ? {
            onMouseEnter: openMenu,
            onMouseLeave: scheduleClose,
            onFocus: openMenu,
            onBlur,
            onKeyDown,
          }
        : {})}
    >
      <Link
        ref={triggerRef}
        to={to}
        className={`cat-link${isSale ? ' is-sale' : ''}${isRescue ? ' is-rescue' : ''}${isActive ? ' is-active' : ''}`}
        {...(hasChildren ? { 'aria-haspopup': true, 'aria-expanded': open } : {})}
        onClick={onTriggerClick}
      >
        {style && <span className="cat-icon" style={style} aria-hidden="true" />}
        <span className="cat-label">{category.name}</span>
        {hasChildren && <ChevronIcon />}
      </Link>

      {hasChildren && open && (
        <div
          ref={panelRef}
          className="cat-mega"
          /*
           * Vyska se strihne na to, co zbyva do spodku okna, a panel uvnitr
           * scrolluje. Bez toho panel jen pretekl pod okraj obrazovky a dolni
           * podkategorie nesly dosahnout — pri deseti detech to nikdo
           * nepoznal, pri tricítce ze stromu dodavatele je to na mobilu
           * polovina nabidky. `top` je z `getBoundingClientRect`, tedy uz
           * vzhledem k oknu, takze staci odecist.
           */
          style={{
            left: pos.left,
            top: pos.top,
            width: `min(calc(100vw - 16px), ${cols * 12}rem)`,
            maxHeight: `calc(100vh - ${Math.round(pos.top)}px - 12px)`,
          }}
          aria-label={category.name}
          onMouseEnter={clearClose}
          onMouseLeave={scheduleClose}
        >
          <div className="cat-mega-grid">
            {category.children.map((child) => (
              <Link
                key={child.slug}
                to={`/?kategorie=${encodeURIComponent(child.slug)}`}
                className={child.slug === activeSlug ? 'cat-sub is-active' : 'cat-sub'}
                onClick={closeMenu}
              >
                {child.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </li>
  );
}

/**
 * Hlavicka se pri odscrollovani zuzi a podlozi stinem.
 *
 * Prah je v pixelech a s hysterezi (`> prah` nahoru, `< prah / 2` dolu):
 * bez ni by hlavicka na hranici blikala pri kazdem drobnem posunu.
 */
function useScrolled(threshold: number): boolean {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    function onScroll() {
      const y = window.scrollY;
      setScrolled((current) => (current ? y > threshold / 2 : y > threshold));
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);

  return scrolled;
}

/**
 * Odkazy na informacni stranky (O nas, obchodni podminky, GDPR…) v paticce.
 * Nactou se jednou; kdyz zadna publikovana neni, sloupec se nevykresli.
 */
function FooterPages() {
  const [pages, setPages] = useState<readonly { slug: string; title: string }[]>([]);

  useEffect(() => {
    let alive = true;
    void api
      .listPages()
      .then((result) => {
        if (alive) setPages(result.pages);
      })
      .catch(() => {
        if (alive) setPages([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (pages.length === 0) return null;

  return (
    <nav className="footnav" aria-label="Informace">
      {pages.map((page) => (
        <Link key={page.slug} to={`/stranka/${page.slug}`}>
          {page.title}
        </Link>
      ))}
    </nav>
  );
}
